<template>
    <div class="nf nf-simple" data-view>
        <div class="nf-bg" aria-hidden="true">
            <span class="nf-blob nf-blob-1"></span>
            <span class="nf-blob nf-blob-2"></span>
            <span class="nf-blob nf-blob-3"></span>
        </div>

        <div class="nf-shell">
            <!-- ══ LATERAL ═══════════════════════════════════════════ -->
            <aside class="nf-side" :aria-label="$t('contact.contact')">
                <div>
                    <h1 class="nf-side-title">{{ $t("contact.contact") }}</h1>
                    <p class="nf-side-sub" v-html="$t('contact.info')"></p>
                </div>
                <p class="nf-side-trust">
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" /><path d="m9 12 2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
                    House &amp; Flats
                </p>
            </aside>

            <!-- ══ PANEL ═════════════════════════════════════════════ -->
            <main class="nf-panel">
                <form v-if="!sent" action="?" class="nf-form" novalidate @submit.prevent="sendMail()">
                    <header class="nf-step-head">
                        <h2 class="nf-step-title">{{ $t("contact.contact") }}</h2>
                        <p class="nf-step-desc" v-html="$t('contact.info')"></p>
                    </header>

                    <!-- Name -->
                    <div class="nf-field">
                        <div class="nf-control">
                            <input id="c-name" v-model="user.name" type="text" placeholder=" " autocomplete="name" required />
                            <label for="c-name">{{ $t("contact.name") }}</label>
                        </div>
                    </div>

                    <!-- Email -->
                    <div class="nf-field">
                        <div class="nf-control">
                            <input id="c-email" v-model="user.email" type="email" placeholder=" " autocomplete="email" required />
                            <label for="c-email">{{ $t("contact.email") }}</label>
                        </div>
                    </div>

                    <!-- Phone -->
                    <div class="nf-field" :class="{ 'is-invalid': $v.user.telefono.$error }">
                        <div class="nf-phone-group">
                            <div class="nf-control nf-control-select">
                                <select id="c-codigo-pais" v-model="user.codigo_pais" name="codigo_pais" required>
                                    <optgroup :label="$t('countries-list.highlighted')">
                                        <option
                                            v-for="(item, i) in highlightedCountriesTranslated"
                                            :key="`c-${i}`"
                                            :data-country-code="item.code"
                                            :value="item.value"
                                        >{{ item.name }}</option>
                                    </optgroup>
                                    <optgroup :label="$t('countries-list.other-countries')">
                                        <option
                                            v-for="(item, i) in countriesWithCodeTranslated"
                                            :key="`o-${i}`"
                                            :data-country-code="item.code"
                                            :value="item.value"
                                        >{{ item.name }}</option>
                                    </optgroup>
                                </select>
                                <label for="c-codigo-pais">{{ $t("contact.phone") }}</label>
                            </div>
                            <div class="nf-control">
                                <input
                                    id="c-telefono"
                                    v-mask="'################'"
                                    v-model="$v.user.telefono.$model"
                                    type="tel"
                                    inputmode="numeric"
                                    placeholder=" "
                                    required
                                    @change="setTelefono($event.target.value)"
                                />
                                <label for="c-telefono">{{ $t("contact.phone") }}</label>
                            </div>
                        </div>
                    </div>

                    <!-- Guest type -->
                    <div class="nf-field">
                        <div class="nf-control nf-control-select">
                            <select id="c-user-type" v-model="user.user_type" required>
                                <option :value="null" disabled></option>
                                <option value="host">{{ $t("contact.user_type.host") }}</option>
                                <option value="guest">{{ $t("contact.user_type.guest") }}</option>
                                <option value="other">{{ $t("contact.user_type.other") }}</option>
                            </select>
                            <label for="c-user-type">{{ $t("contact.user_type.label") }}</label>
                        </div>
                    </div>

                    <!-- Destination/Accommodation country -->
                    <div class="nf-field">
                        <div class="nf-control nf-control-select">
                            <select id="c-destination" v-model="user.destination_country" name="destination_country" required>
                                <optgroup>
                                    <option
                                        v-for="(item, i) in countriesTranslated"
                                        :key="`d-${i}`"
                                        :data-country-code="item.code"
                                    >{{ item.name }}</option>
                                </optgroup>
                            </select>
                            <label for="c-destination">
                                <template v-if="user.user_type === 'host'">{{ $t("contact.accommodation-country") }}</template>
                                <template v-else-if="user.user_type === 'guest'">{{ $t("contact.destination-country") }}</template>
                                <template v-else>{{ $t("contact.interested-in-country") }}</template>
                            </label>
                        </div>
                    </div>

                    <!-- Message -->
                    <div class="nf-field">
                        <div class="nf-control nf-control-textarea">
                            <textarea id="c-message" v-model="user.message" placeholder=" " rows="4" required></textarea>
                            <label for="c-message">{{ $t("contact.message") }}</label>
                        </div>
                    </div>

                    <!-- Captcha -->
                    <div class="nf-recaptcha">
                        <div class="g-recaptcha" data-sitekey="6LdH4vAZAAAAAIiZSVZVJAn9ZyuOwTjklSMRZV5s"></div>
                    </div>

                    <!-- Send button -->
                    <div class="nf-nav nf-nav-single">
                        <button type="submit" class="nf-btn nf-btn-primary" id="contact-send-button" :disabled="loading">
                            <b-spinner v-if="loading" label="Spinning" style="width: 20px; height: 20px"></b-spinner>
                            <span>{{ $t("contact.send") }}</span>
                        </button>
                    </div>
                </form>

                <section v-else class="nf-screen" aria-live="polite">
                    <div class="nf-success-badge" aria-hidden="true">
                        <svg viewBox="0 0 52 52" width="76" height="76">
                            <circle class="nf-check-circle" cx="26" cy="26" r="24" fill="none" />
                            <path class="nf-check-mark" fill="none" d="M15 27l7 7 15-15" />
                        </svg>
                    </div>
                    <h2 class="nf-success-title">{{ $t("contact.sent") }}</h2>
                </section>
            </main>
        </div>
    </div>
</template>

<script>
import {
	required,
	email,
	sameAs,
	alphaNum,
	minValue,
	minLength,
	maxLength,
	alpha,
	and,
	numeric,
} from "vuelidate/lib/validators";
import { mask } from "vue-the-mask";
import axios from "axios";
import router from "@/router";
import apiUrls from "@/new-constants/apiUrls";
import countryMixin from "@/mixins/countryMixin";

export default {
    name: 'Contact',
    mixins: [ countryMixin ],
	data() {
		return {
			user: {
				telefono: "",
				email: "",
				name: "",
				codigo_pais: null,
				message: "",
                destination_country: null,
                user_type: null,
            },
			errors: {
				telefono: false,
			},
			sent: false,
			loading: false,
		};
	},
	directives: { mask },
	methods: {
		setTelefono(value) {
			this.user.telefono = value;
			this.$v.user.telefono.$touch();
		},
		sendMail() {
			if (
				grecaptcha.getResponse() !== "" &&
				grecaptcha.getResponse() !== null
			) {
				this.loading = true;
				this.user.token = grecaptcha.getResponse();
				axios
					.post(
						apiUrls.recaptcha,
						this.user
					)
					.then((response) => {
						if (response.data.success) {
							axios
								.post(
									apiUrls.contact,
									this.user
								)
								.then((value) => {
									this.sent = true;
									this.loading = false;
									setTimeout(function () {
										router.push({ path: "/" });
									}, 5000);
								});
						} else {
							alert(
								"Recaptcha token inválido. Inténtalo de nuevo."
							);
						}
					});
			} else {
				alert("Acepta el recaptcha.");
			}
		},
	},
	validations: {
		user: {
			telefono: {
				numeric,
			},
		},
	},
	created() {
		window.scrollTo(0, 0);
	},
    computed: {
    },
	mounted() {
		let recaptchaScript = document.createElement("script");
		recaptchaScript.setAttribute(
			"src",
			"https://www.google.com/recaptcha/api.js"
		);
		document.head.appendChild(recaptchaScript);
	},
};
</script>

<!-- Reutiliza el molde visual de /formulario (todo bajo .nf) -->
<style src="../../components/Formulario/formularioNuevo.css"></style>

<style lang="scss" scoped>
/* Ajustes propios de los formularios simples (contacto / inversión) que
   reusan el molde .nf pero no son wizard: el panel no necesita el ancho del
   lateral fijo cuando el contenido es corto, y los <select> no existen como
   control nativo en el molde (allá usan NfCombo), así que se estilan acá
   para que combinen con los .nf-control de texto. */

/* El panel centra la card y le deja aire arriba */
::v-deep .nf-simple .nf-panel {
    justify-content: center;
    padding-top: 48px;
}

/* Select con el mismo look que .nf-control input + label flotante.
   El label siempre va arriba (el select no tiene placeholder vacío). */
::v-deep .nf-control-select {
    position: relative;
}
::v-deep .nf-control-select select {
    display: block;
    width: 100%;
    height: 60px;
    padding: 24px 40px 8px 16px;
    border: 1.5px solid var(--line-2);
    border-radius: var(--radius);
    background: var(--surface);
    font-size: 16px;
    color: var(--ink);
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    transition: border-color .2s, box-shadow .2s, background .2s;
    /* Flecha dibujada */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23e8640a' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
}
::v-deep .nf-control-select select:hover { border-color: #cbbcae; }
::v-deep .nf-control-select select:focus {
    border-color: var(--orange-500);
    box-shadow: var(--ring);
    background-color: #fffdfb;
}
::v-deep .nf-control-select label {
    position: absolute;
    left: 17px;
    top: 18px;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted);
    pointer-events: none;
    max-width: calc(100% - 50px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
::v-deep .nf-field.is-invalid .nf-control-select select { border-color: var(--danger); background-color: #fffafa; }
::v-deep .nf-field.is-invalid .nf-control-select label { color: var(--danger); }

/* reCAPTCHA centrado con aire */
::v-deep .nf-recaptcha {
    display: flex;
    justify-content: center;
    margin: 8px 0 4px;
}

/* Barra de un solo botón (no hay "atrás"): el primario a la derecha */
::v-deep .nf-nav-single {
    justify-content: flex-end;
}
</style>
